import { MessageType } from 'ts-components/Chat';
import { QuickResponseData } from 'ts-components/QuickResponses';

export interface AIChatSession {
  id: string;
  graph: string;
  tag?: string;
  status: 'active' | 'completed' | 'error';
  turn_count: number;
  metadata?: Record<string, unknown>;
  created: string;
}

export interface CreateSessionPayload {
  graph: string;
  tag?: string;
  [key: string]: unknown;
}

export interface CreateSessionResponse {
  session_id: string;
  status: 'active';
  welcome_message?: string;
}

export interface HistoryMessage {
  turn_count: number;
  role: 'user' | 'assistant';
  space?: string;
  mode?: string;
  content?: string;
  assistant_message?: string;
  user_message?: string;
  done?: boolean;
  usage?: {
    input_tokens: number;
    output_tokens: number;
    tool_calls: number;
  };
  alert?: string;
}

export type ConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'error';

export type SpaceName =
  | 'status_summary'
  | 'status_details'
  | 'assistant_message'
  | 'user_message'
  | 'placeholder'
  | 'button'
  | 'buttons'
  | 'previews';

export type ButtonActionType = 'url' | 'single_select' | 'multi_select';

export interface BackendButton {
  text: string;
  type: ButtonActionType;
  chat_message?: string;
  url?: string;
}

export interface ButtonOption {
  label: string;
  value: string;
  url?: string;
}

export interface ButtonSpaceData {
  actionType: ButtonActionType;
  options: ButtonOption[];
  header?: string;
}

export interface AcceptedEventData {
  turn_count: number;
}

export interface StatusEventData {
  turn_count: number;
  space?: SpaceName;
  mode?: string;
  content?: string;
  done: boolean;
  new_frame: boolean;
  id: string;
  usage?: UsageData;
  alert?: string;
  alert_type?: string;
  error?: string;
}

export interface UsageData {
  input_tokens: number;
  output_tokens: number;
  tool_calls: number;
}

export interface TokenEventData {
  token: string;
  space?: SpaceName;
}

export interface ErrorEventData {
  status_code: number;
  message?: string;
  error?: string;
  recoverable?: boolean;
}

export interface HistoryEventData {
  messages: HistoryMessage[];
}

export interface FilterPreviewData {
  filter_config?: Record<string, unknown>;
  filter_name?: string;
}

export interface FilterPreviewItem {
  type: 'filter';
  label: string;
  data: FilterPreviewData;
  url?: string;
  icon?: string;
}

export interface CustomObjectPreviewData {
  object_id?: string;
}

export interface CustomObjectPreviewItem {
  type: 'custom_object';
  label: string;
  data: CustomObjectPreviewData;
  url?: string;
  icon?: string;
}

export interface UnknownPreviewItem {
  type: string;
  label: string;
  data: Record<string, unknown>;
  url?: string;
  icon?: string;
}

export type PreviewItem =
  | FilterPreviewItem
  | CustomObjectPreviewItem
  | UnknownPreviewItem;

export interface AIChatConfig {
  graph: string;
  tag?: string;
  metadata?: Record<string, unknown>;
}

export interface AIChatContextValue {
  messages: MessageType[];
  quickResponseData: QuickResponseData;
  placeholder: string;
  connectionState: ConnectionState;
  isStreaming: boolean;
  isLoadingHistory: boolean;
  sessionId: string | null;
  turnCount: number;
  config: AIChatConfig;
  alert: string;
  previewData: PreviewItem[];
  sendMessage: (text: string) => Promise<void>;
  handleQuickResponseSelect: (
    value: string,
    type: string,
    url?: string
  ) => void;
  createSession: (payload?: CreateSessionPayload) => Promise<string>;
  reconnect: () => Promise<void>;
  resetChat: () => void;
}

export interface AIChatProviderProps {
  children: React.ReactNode;
  config: AIChatConfig;
  sessionId?: string;
  autoConnect?: boolean;
  initialMessage?: string;
  freshSession?: boolean;
  onError?: (error: Error) => void;
  onMessageComplete?: (turnCount: number) => void;
  onSessionCreated?: (sessionId: string) => void;
}

export interface AIChatAPI {
  resetChat: () => void;
}

export interface AIChatContainerProps {
  header?: string;
  showLogo?: boolean;
  headerLogoUrl?: string | null;
  renderMarkdown?: boolean;
  hideRefresh?: boolean;
  hideQuickActions?: boolean;
  showFeedbackIcons?: boolean;
  sampleIdeas?: QuickResponseData;
  enableQuickActionButtons?: boolean;
  alwaysShowStartOver?: boolean;
  additionalActions?: Array<{ label: string; value: string }>;
  onActionSelect?: (value: string) => void;
  onCreateNewChat?: (context?: Record<string, unknown>) => void;
  headerMenuOptions?: Array<{ value: string; label: string }>;
  onHeaderMenuOptionSelect?: (option: { value: string }) => void;
  onReady?: (api: AIChatAPI) => void;
  onCopyMessage?: (messageContent: string) => void;
  onLikeMessage?: () => void;
  onDislikeMessage?: () => void;
  onRefreshMessage?: () => void;
  onPreviewNavigate?: (preview: PreviewItem) => void;
}

export interface AIChatProps extends AIChatContainerProps {
  config: AIChatConfig;
  sessionId?: string;
  autoConnect?: boolean;
  initialMessage?: string;
  freshSession?: boolean;
  onError?: (error: Error) => void;
  onMessageComplete?: (turnCount: number) => void;
  onSessionCreated?: (sessionId: string) => void;
}

export interface MessageStreamYield {
  messages: MessageType[];
  turnCount: number;
  isComplete: boolean;
  quickResponseData: QuickResponseData;
  placeholder: string;
  previewData: PreviewItem[];
}

export class AIChatStreamError extends Error {
  public statusCode: number;
  public recoverable: boolean;

  constructor(data: ErrorEventData) {
    super(data.error || data.message || 'Unknown error');
    this.name = 'AIChatStreamError';
    this.statusCode = data.status_code;
    this.recoverable = data.recoverable ?? false;
  }
}

export interface UseChatStreamOptions {
  sessionId: string | null;
  onError?: (error: Error) => void;
  onComplete?: (turnCount: number) => void;
}

export interface UseChatStreamReturn {
  messages: MessageType[];
  quickResponseData: QuickResponseData;
  placeholder: string;
  turnCount: number;
  isStreaming: boolean;
  connectionState: ConnectionState;
  alert: string;
  previewData: PreviewItem[];
  sendMessage: (text: string, sessionIdOverride?: string) => void;
  rehydrate: (historyEvents: HistoryMessage[]) => void;
  resumeStream: (sessionIdOverride?: string) => void;
  abort: () => void;
  setWelcomeMessage: (content: string) => void;
}
